import React from "react";
import ServiceLayout from "../components/ServiceLayout";

const PantryManagement = () => {
  const whatsIncluded = [
    {
      title: "Barista-Grade Coffee",
      description: "Espresso, cappuccino, latte, cold brew, and pour-over using freshly roasted single-origin beans."
    },
    {
      title: "Artisanal Tea Station",
      description: "50+ varieties including Darjeeling first flush, Japanese matcha, and herbal wellness blends."
    },
    {
      title: "Healthy Snack Pantry",
      description: "Curated selection of nuts, dried fruits, protein bars, and regional snacks with dietary labels."
    },
    {
      title: "Fresh Fruit Program",
      description: "Seasonal fruit baskets delivered daily — local and exotic varieties."
    },
    {
      title: "SLA-Backed Service",
      description: "Guaranteed replenishment within 24 hours. Real-time inventory tracking via dashboard."
    },
    {
      title: "Dedicated Pantry Staff",
      description: "Trained pantry managers who maintain hygiene, stock levels, and equipment."
    }
  ];

  const perfectFor = [
    "Tech startups and scale-ups",
    "Creative agencies and studios",
    "Co-working spaces",
    "Consulting firms",
    "Financial services offices",
    "Healthcare administration"
  ];

  const features = [
    {
      title: "Coffee Bar",
      description: "Barista-grade espresso, cappuccino & cold brew",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><path d="M18 8h1a4 4 0 010 8h-1M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><path d="M6 1v3M10 1v3M14 1v3"/></svg>
    },
    {
      title: "Tea Station",
      description: "Artisanal teas from Darjeeling to Assam",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><path d="M17 8h1a4 4 0 010 8h-1M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4V8z"/><path d="M6 1v3M10 1v3M14 1v3"/></svg>
    },
    {
      title: "Snack Pantry",
      description: "Healthy, curated snacks with dietary labels",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
    },
    {
      title: "SLA Backed",
      description: "Guaranteed replenishment within 24 hours",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
    }
  ];

  const faqs = [
    { question: 'What can office pantry management include?', answer: 'The service can include pantry staffing, beverages, snacks, supplies, replenishment and routine pantry upkeep, depending on the agreed scope.' },
    { question: 'Can pantry service be customized for our office?', answer: 'Yes. The operating model can be planned around your team size, office setup, consumption pattern and service requirements.' },
    { question: 'Do you provide office pantry services in Bangalore?', answer: 'Yes. We work with corporate and workplace requirements across Bengaluru, subject to location and scope.' }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": 'Office Pantry Management Services in Bangalore',
      "serviceType": 'Corporate office pantry management',
      "url": 'https://theofficemealco.com/pantry-management',
      "provider": { "@type": "Organization", "name": "The Office Meal Co.", "url": "https://theofficemealco.com/" },
      "areaServed": { "@type": "City", "name": "Bengaluru" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question", "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    }
  ];

  return (
    <ServiceLayout
      title="Office Pantry Management Services in Bangalore"
      subtitle="Corporate pantry services for Bangalore offices, covering pantry staffing, beverages, snacks, supplies, hygiene and day-to-day pantry operations."
      heroImage="/images/pantry-management.png"
      whatsIncluded={whatsIncluded}
      perfectFor={perfectFor}
      seoTitle="Office Pantry Management Services Bangalore"
      seoDescription="Office pantry management in Bangalore with pantry staffing, coffee and tea service, healthy snacks, inventory control and SLA-backed replenishment."
      seoKeywords="office pantry management bangalore, office pantry services bangalore, corporate pantry services, pantry management"
      canonicalUrl="https://theofficemealco.com/pantry-management"
      structuredData={structuredData}
      features={features}
    >
      {/* Custom content */}
      <div className="mb-16">
        <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
           • The Setup
        </span>
        <h2 className="font-serif text-3xl text-[#1E3A5F] mb-8">
          End-to-end pantry management for Bangalore offices
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white p-8 shadow-sm border-t-4 border-[#CBA365]">
            <h3 className="font-serif text-xl text-[#1E3A5F] mb-4">Design</h3>
            <p className="text-[#64748B] mb-4 font-sans">
              We assess your space, footfall, and preferences to design an optimal pantry layout. 
              From compact kitchenettes to full-service cafeterias.
            </p>
            <ul className="space-y-2 text-sm text-[#64748B] font-sans">
              <li>• Space optimization</li>
              <li>• Equipment selection</li>
              <li>• Workflow design</li>
            </ul>
          </div>

          <div className="bg-white p-8 shadow-sm border-t-4 border-[#1E3A5F]">
            <h3 className="font-serif text-xl text-[#1E3A5F] mb-4">Staffing</h3>
            <p className="text-[#64748B] mb-4 font-sans">
              Trained pantry managers, baristas, and support staff who become part of your office culture. 
              Background-verified and uniformed.
            </p>
            <ul className="space-y-2 text-sm text-[#64748B] font-sans">
              <li>• Dedicated pantry manager</li>
              <li>• Certified baristas</li>
              <li>• Hygiene officers</li>
            </ul>
          </div>

          <div className="bg-white p-8 shadow-sm border-t-4 border-[#CBA365]">
            <h3 className="font-serif text-xl text-[#1E3A5F] mb-4">Technology</h3>
            <p className="text-[#64748B] mb-4 font-sans">
              Real-time inventory tracking, consumption analytics, and automated reordering. 
              Know exactly what's being consumed and when.
            </p>
            <ul className="space-y-2 text-sm text-[#64748B] font-sans">
              <li>• Inventory dashboard</li>
              <li>• Consumption analytics</li>
              <li>• Automated reordering</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Equipment Showcase */}
      <div className="bg-[#F3EFE6] p-8 md:p-12 mb-16">
        <h3 className="font-serif text-2xl text-[#1E3A5F] mb-8 text-center">Equipment & Supplies</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: "Espresso Machine", icon: "☕" },
            { name: "Cold Brew Tower", icon: "🧊" },
            { name: "Tea Dispensers", icon: "🫖" },
            { name: "Snack Organizers", icon: "🥜" },
            { name: "Fruit Display", icon: "🍎" },
            { name: "Water Station", icon: "💧" },
            { name: "Microwave Oven", icon: "🔥" },
            { name: "Refrigerator", icon: "❄️" }
          ].map((item, i) => (
            <div key={i} className="bg-white p-4 text-center shadow-sm">
              <span className="text-3xl block mb-2">{item.icon}</span>
              <span className="text-sm text-[#1E3A5F] font-sans">{item.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* SLA Promise */}
      <div className="border-2 border-[#1E3A5F] p-8 md:p-12 text-center bg-white">
        <h3 className="font-serif text-3xl text-[#1E3A5F] mb-4">Our SLA Promise</h3>
        <div className="grid md:grid-cols-3 gap-8 mt-8">
          <div>
            <span className="font-serif text-4xl text-[#CBA365] block mb-2">24h</span>
            <span className="text-[#64748B] font-sans">Replenishment guarantee</span>
          </div>
          <div>
            <span className="font-serif text-4xl text-[#CBA365] block mb-2">99%</span>
            <span className="text-[#64748B] font-sans">Stock availability</span>
          </div>
          <div>
            <span className="font-serif text-4xl text-[#CBA365] block mb-2">0</span>
            <span className="text-[#64748B] font-sans">Stockout tolerance</span>
          </div>
        </div>
      </div>

      <div className="mt-16 bg-[#F3EFE6] p-8 md:p-12">
        <h2 className="font-serif text-3xl text-[#1E3A5F] mb-8">Frequently asked questions</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {faqs.map((faq) => (
            <div key={faq.question} className="bg-white p-6 border border-[#E8DDCB]">
              <h3 className="font-medium text-[#1E3A5F] mb-3 font-sans">{faq.question}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed font-sans">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </ServiceLayout>
  );
};

export default PantryManagement;
